import { forwardRef, type ReactNode, type SelectHTMLAttributes } from 'react';
import clsx from 'clsx';

import { ChevronDownIcon } from '../internal/icons';
import { useMergedFieldAria } from './FormField';
import styles from './Select.module.css';
import type { FieldSize } from './TextInput';

export interface SelectOption {
  value: string;
  label: string;
  disabled?: boolean;
}

export interface SelectProps extends Omit<SelectHTMLAttributes<HTMLSelectElement>, 'size'> {
  size?: FieldSize;
  invalid?: boolean;
  /** Options as data. Use `children` instead when you need option groups. */
  options?: readonly SelectOption[];
  /** Rendered first, disabled and selected-by-default when there is no value. */
  placeholder?: string;
  children?: ReactNode;
}

/**
 * A native `select`. Deliberately not a custom listbox: the native control gets
 * keyboard, type-ahead, mobile pickers and forced-colours right, and nothing in
 * the mockups needs a richer option row.
 */
export const Select = forwardRef<HTMLSelectElement, SelectProps>(function Select(
  { size = 'md', invalid = false, options, placeholder, className, children, ...rest },
  ref,
) {
  const aria = useMergedFieldAria({
    id: rest.id,
    describedBy: rest['aria-describedby'],
    invalid,
    required: rest.required,
  });

  return (
    <span className={clsx(styles.wrapper, styles[size])}>
      <select {...rest} {...aria} ref={ref} className={clsx(styles.select, className)}>
        {placeholder !== undefined && (
          <option value="" disabled>
            {placeholder}
          </option>
        )}
        {options?.map((option) => (
          <option key={option.value} value={option.value} disabled={option.disabled}>
            {option.label}
          </option>
        ))}
        {children}
      </select>
      <ChevronDownIcon className={styles.caret} />
    </span>
  );
});
